/**
 * TestCaseExport Component
 * Allows users to export test scenarios to test cases
 * Supports individual selection, folder-level export, and tag-based filtering
 */

import React, { useState, useEffect } from 'react';
import { apiClient, TestScenarioWithEvidence, TestCase, TestCaseExportRequest } from '../services/api';
import './TestCaseExport.css';

interface TestCaseExportProps {
  folderId?: string;
  onExportComplete?: (testCases: TestCase[]) => void;
}

export const TestCaseExport: React.FC<TestCaseExportProps> = ({ folderId, onExportComplete }) => {
  const [scenarios, setScenarios] = useState<TestScenarioWithEvidence[]>([]);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [exportMode, setExportMode] = useState<'individual' | 'folder' | 'tags'>('individual');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [exportedTestCases, setExportedTestCases] = useState<TestCase[]>([]);

  /**
   * Load all scenarios from API for the folder
   */
  const loadScenarios = async () => {
    if (!folderId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getScenariosByFolder(folderId);
      setScenarios(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  // Load scenarios when folder changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (folderId) {
      loadScenarios();
    }
  }, [folderId]);

  // Extract all unique tags when scenarios change
  useEffect(() => {
    const tags = new Set<string>();
    scenarios.forEach((scenario) => {
      scenario.tags.forEach((tag) => tags.add(tag));
    });
    setAllTags(Array.from(tags).sort());
  }, [scenarios]);
  /**
   * Toggle scenario selection
   */
  const toggleScenarioSelection = (scenarioId: string) => {
    if (selectedScenarioIds.includes(scenarioId)) {
      setSelectedScenarioIds(selectedScenarioIds.filter((id) => id !== scenarioId));
    } else {
      setSelectedScenarioIds([...selectedScenarioIds, scenarioId]);
    }
  };

  /**
   * Select all scenarios
   */
  const selectAllScenarios = () => {
    setSelectedScenarioIds(scenarios.map((s) => s.id));
  };

  /**
   * Deselect all scenarios
   */
  const deselectAllScenarios = () => {
    setSelectedScenarioIds([]);
  };

  /**
   * Toggle tag selection
   */
  const toggleTagSelection = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  /**
   * Clear all tag selections
   */
  const clearTagSelections = () => {
    setSelectedTags([]);
  };

  /**
   * Show export confirmation dialog
   */
  const showExportConfirmation = () => {
    // Validate selection based on export mode
    if (exportMode === 'individual' && selectedScenarioIds.length === 0) {
      setError('Please select at least one scenario to export');
      return;
    }
    if (exportMode === 'tags' && selectedTags.length === 0) {
      setError('Please select at least one tag to filter scenarios');
      return;
    }
    if (exportMode === 'folder' && !folderId) {
      setError('No folder selected for export');
      return;
    }

    setError(null);
    setShowConfirmation(true);
  };

  /**
   * Cancel export
   */
  const cancelExport = () => {
    setShowConfirmation(false);
  };

  /**
   * Confirm and execute export
   */
  const confirmExport = async () => {
    setIsLoading(true);
    setError(null);
    setShowConfirmation(false);

    try {
      // Build export request based on mode
      const request: TestCaseExportRequest = {};

      if (exportMode === 'individual') {
        request.scenario_ids = selectedScenarioIds;
      } else if (exportMode === 'folder') {
        request.folder_id = folderId;
      } else if (exportMode === 'tags') {
        request.tags = selectedTags;
      }

      const testCases = await apiClient.exportTestCases(request);
      setExportedTestCases(testCases);
      
      // Reset selections
      setSelectedScenarioIds([]);
      setSelectedTags([]);
      
      // Notify parent component
      onExportComplete?.(testCases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export test cases');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get count of scenarios that will be exported
   */
  const getExportCount = (): number => {
    if (exportMode === 'individual') {
      return selectedScenarioIds.length;
    } else if (exportMode === 'folder') {
      return scenarios.length;
    } else if (exportMode === 'tags') {
      // Count scenarios that have at least one of the selected tags
      return scenarios.filter((s) =>
        selectedTags.some((tag) => s.tags.includes(tag))
      ).length;
    }
    return 0;
  };

  /**
   * Get priority badge class
   */
  const getPriorityClass = (priority: string): string => {
    switch (priority) {
      case 'Critical':
        return 'priority-critical';
      case 'High':
        return 'priority-high';
      case 'Medium':
        return 'priority-medium';
      case 'Low':
        return 'priority-low';
      default:
        return '';
    }
  };

  return (
    <div className="test-case-export">
      <div className="export-header">
        <h2>Export Test Cases</h2>
        <p>Select scenarios to export as executable test cases</p>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {exportedTestCases.length > 0 && (
        <div className="success-message" role="status">
          Successfully exported {exportedTestCases.length} test case(s)!
        </div>
      )}

      {/* Export Mode Selection */}
      <div className="export-mode-selector">
        <h3>Export Mode</h3>
        <div className="mode-options">
          <label className={`mode-option ${exportMode === 'individual' ? 'active' : ''}`}>
            <input
              type="radio"
              name="exportMode"
              value="individual"
              checked={exportMode === 'individual'}
              onChange={() => setExportMode('individual')}
              disabled={isLoading}
            />
            <div className="mode-content">
              <strong>Individual Selection</strong>
              <span>Select specific scenarios to export</span>
            </div>
          </label>

          <label className={`mode-option ${exportMode === 'folder' ? 'active' : ''}`}>
            <input
              type="radio"
              name="exportMode"
              value="folder"
              checked={exportMode === 'folder'}
              onChange={() => setExportMode('folder')}
              disabled={isLoading || !folderId}
            />
            <div className="mode-content">
              <strong>Entire Folder</strong>
              <span>Export all scenarios in the current folder</span>
            </div>
          </label>

          <label className={`mode-option ${exportMode === 'tags' ? 'active' : ''}`}>
            <input
              type="radio"
              name="exportMode"
              value="tags"
              checked={exportMode === 'tags'}
              onChange={() => setExportMode('tags')}
              disabled={isLoading || allTags.length === 0}
            />
            <div className="mode-content">
              <strong>Tag-Based Filter</strong>
              <span>Export scenarios matching selected tags</span>
            </div>
          </label>
        </div>
      </div>

      {/* Individual Selection Mode */}
      {exportMode === 'individual' && (
        <div className="export-section">
          <div className="section-header">
            <h3>Select Scenarios</h3>
            <div className="selection-actions">
              <button
                className="btn-link"
                onClick={selectAllScenarios}
                disabled={isLoading || scenarios.length === 0}
              >
                Select All
              </button>
              <button
                className="btn-link"
                onClick={deselectAllScenarios}
                disabled={isLoading || selectedScenarioIds.length === 0}
              >
                Deselect All
              </button>
            </div>
          </div>

          {isLoading && scenarios.length === 0 ? (
            <div className="loading">Loading scenarios...</div>
          ) : scenarios.length === 0 ? (
            <div className="empty-state">
              <p>No scenarios available in this folder.</p>
            </div>
          ) : (
            <div className="scenario-selection-list">
              {scenarios.map((scenario) => (
                <label
                  key={scenario.id}
                  className={`scenario-checkbox-item ${
                    selectedScenarioIds.includes(scenario.id) ? 'selected' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedScenarioIds.includes(scenario.id)}
                    onChange={() => toggleScenarioSelection(scenario.id)}
                    disabled={isLoading}
                  />
                  <div className="scenario-info">
                    <div className="scenario-title">
                      {scenario.scenario_description.substring(0, 100)}
                      {scenario.scenario_description.length > 100 && '...'}
                    </div>
                    <div className="scenario-meta">
                      <span className={`priority-badge ${getPriorityClass(scenario.priority)}`}>
                        {scenario.priority}
                      </span>
                      {scenario.module_name && (
                        <span className="module-badge">{scenario.module_name}</span>
                      )}
                      <span className="version-badge">v{scenario.version}</span>
                      {scenario.tags.length > 0 && (
                        <span className="tags-preview">
                          {scenario.tags.slice(0, 3).join(', ')}
                          {scenario.tags.length > 3 && '...'}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="selection-summary">
            Selected: {selectedScenarioIds.length} of {scenarios.length} scenarios
          </div>
        </div>
      )}

      {/* Folder Export Mode */}
      {exportMode === 'folder' && (
        <div className="export-section">
          <h3>Folder Export</h3>
          <div className="folder-export-info">
            <p>
              All <strong>{scenarios.length}</strong> scenario(s) in this folder will be exported as test cases.
            </p>
            {scenarios.length === 0 && (
              <div className="empty-state">
                <p>No scenarios available in this folder.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tag-Based Filter Mode */}
      {exportMode === 'tags' && (
        <div className="export-section">
          <div className="section-header">
            <h3>Select Tags</h3>
            {selectedTags.length > 0 && (
              <button
                className="btn-link"
                onClick={clearTagSelections}
                disabled={isLoading}
              >
                Clear Selection
              </button>
            )}
          </div>

          {allTags.length === 0 ? (
            <div className="empty-state">
              <p>No tags available. Add tags to scenarios to use tag-based filtering.</p>
            </div>
          ) : (
            <>
              <div className="tag-selection-list">
                {allTags.map((tag) => (
                  <label
                    key={tag}
                    className={`tag-checkbox-item ${selectedTags.includes(tag) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={() => toggleTagSelection(tag)}
                      disabled={isLoading}
                    />
                    <span className="tag-name">{tag}</span>
                  </label>
                ))}
              </div>

              <div className="tag-filter-summary">
                <p>
                  {getExportCount()} scenario(s) match the selected tag(s)
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Export Action */}
      <div className="export-actions">
        <button
          className="btn btn-primary btn-lg"
          onClick={showExportConfirmation}
          disabled={isLoading || getExportCount() === 0}
        >
          {isLoading ? 'Exporting...' : `Export ${getExportCount()} Test Case(s)`}
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="modal-overlay" onClick={cancelExport}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Export</h3>
              <button className="modal-close" onClick={cancelExport} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                You are about to export <strong>{getExportCount()}</strong> scenario(s) as test cases.
              </p>
              <p>
                {exportMode === 'individual' && 'The selected scenarios will be exported.'}
                {exportMode === 'folder' && 'All scenarios in this folder will be exported.'}
                {exportMode === 'tags' && 'All scenarios matching the selected tags will be exported.'}
              </p>
              <p className="confirmation-note">
                This will create new test case instances that can be executed independently.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelExport} disabled={isLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmExport} disabled={isLoading}>
                {isLoading ? 'Exporting...' : 'Confirm Export'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
